import { ImoovaImage } from "@/app/Media/Components/Image";
import { useState } from "react";
import { useTranslations } from "use-intl";
import { MediaCollection, ModelType } from "@/gql/graphql";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import {
  EditableRowsProvider,
  EditableSettingRow,
  SettingRowsCard,
} from "@/app/Settings/Settings/Components/EditableSettingRow";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { createMediaMutation } from "@/app/Media/GraphQL/mediaMutations";
import { UserCircleIcon } from "@heroicons/react/24/solid";
import { FileUploader } from "@/lib/FileUpload/FileUploader";
import {
  OrganisationSettingsRecord,
  organisationSettingsQuery,
  updateCurrentOrganisationMutation,
} from "@/app/Settings/Settings/GraphQL/organisationSettingsQuery";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { Dialog, DialogTitle } from "@/components/catalyst/dialog";
import { OrganizationMembersList } from "../Components/OrganizationMembersList";
import {
  OrganizationInvitationsList,
  organisationInvitationsQuery,
} from "../Components/OrganizationInvitationsList";
import { Navbar, NavbarItem } from "@/components/catalyst/navbar";
import { useActiveOrganisation } from "@/app/Organisations/Hooks/useActiveOrganisation";

export function SettingsTeamScreen() {
  "use no memo";

  const t = useTranslations("settings.team");
  const { activeOrganisationId } = useActiveOrganisation();
  const [activeTab, setActiveTab] = useState<"members" | "invitations">(
    "members",
  );

  const { data } = useGqlQuery(organisationSettingsQuery, {});

  // Shares the invitations card's query key, so this only reads the cache for the
  // tab count — no extra request.
  const { data: invitationsData } = useGqlQuery(organisationInvitationsQuery, {
    organisation_id: activeOrganisationId!,
    first: 20,
    page: 1,
  });
  const invitationCount =
    invitationsData?.organisationInvitations?.paginatorInfo?.total ?? 0;

  const organisation = data?.currentOrganisation;

  return (
    <div className="space-y-12 p-6">
      <div className="space-y-6">
        <div className="border-b border-zinc-200 dark:border-zinc-800">
          <Navbar>
            <NavbarItem
              current={activeTab === "members"}
              onClick={() => setActiveTab("members")}
            >
              {t("members.title")}
            </NavbarItem>
            <NavbarItem
              current={activeTab === "invitations"}
              onClick={() => setActiveTab("invitations")}
            >
              {t("invitations.title")} ({invitationCount})
            </NavbarItem>
          </Navbar>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6">
          {activeTab === "members" ? (
            <OrganizationMembersList />
          ) : (
            <OrganizationInvitationsList />
          )}
        </div>
      </div>

      {organisation ? (
        <OrganisationProfileSection organisation={organisation} />
      ) : null}
    </div>
  );
}

function OrganisationProfileSection({
  organisation,
}: {
  organisation: OrganisationSettingsRecord;
}) {
  const { mutateAsync } = useGqlMutation(updateCurrentOrganisationMutation);

  return (
    <EditableRowsProvider>
      <SettingRowsCard
        title="Organisation profile"
        description="Update your organisation's name and logo."
      >
        <EditableSettingRow
          label="Organisation name"
          value={organisation.name}
          initialValues={{
            name: organisation.name,
          }}
          onSave={async (values) => {
            return mutateAsync({
              input: values,
            });
          }}
        >
          <TextInput name="name" label="Organisation name" />
        </EditableSettingRow>

        <OrganisationLogo organisation={organisation} />
      </SettingRowsCard>
    </EditableRowsProvider>
  );
}

function OrganisationLogo({
  organisation,
}: {
  organisation: OrganisationSettingsRecord;
}) {
  const { mutateAsync: createMedia } = useGqlMutation(createMediaMutation);
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const contactId = organisation.contact?.id;

  if (!contactId) {
    return null;
  }

  return (
    <div className="flex items-center gap-4 px-4 py-3 sm:px-6">
      <div className="w-40 shrink-0 text-sm font-medium text-gray-500">
        Logo
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-x-3">
        {organisation.profilePicture ? (
          <ImoovaImage
            media={organisation.profilePicture}
            alt="logo"
            className="h-10"
          />
        ) : (
          <UserCircleIcon className="h-10 w-10 text-gray-300" aria-hidden />
        )}
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="shadow-xs shrink-0 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
      >
        Change
      </button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Upload logo</DialogTitle>
        <div className="relative w-full py-1">
          <FileUploader
            onDrop={() => setIsUploading(true)}
            onUpload={async (uploads) => {
              if (uploads.length === 0 || uploads.length > 1) {
                throw new Error("Uploads must be exactly 1");
              }
              const upload = uploads[0];

              await createMedia({
                input: {
                  file_name: upload.file.name,
                  key: upload.filePath,
                  collectionName: MediaCollection.ProfilePicture,
                  objectType: ModelType.Contact,
                  objectId: contactId,
                },
              });

              setOpen(false);
              setIsUploading(false);
            }}
          />
          {isUploading ? (
            <div className="backdrop-blur-xs absolute inset-0 flex items-center justify-center bg-white/30">
              <Spinner />
            </div>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
}
